/**
 * Display mapping for the operational documents list (Dokumente 2.0, S5).
 *
 * WHAT THIS IS FOR. The list has always carried `mime_type` on the wire and
 * rendered it nowhere, so every row looked alike and a reader scanning for
 * "the scan" among forty contracts had only the title to go on. S5 turns
 * that field into the one thing a file list is supposed to show at a
 * glance: what KIND of file each row is.
 *
 * WHY IT IS ITS OWN FILE. `AdminDocumentsPage.js` is already 982 lines, and
 * CLAUDE.md Regel 48 asks for an extract to be considered before new logic
 * lands in a file that size. This is also the repo's own shape for
 * feature-local display helpers — `pages/admin/unit/unitDetailHelpers.js`
 * and `pages/admin/tenant/tenantDetailHelpers.js` both keep a
 * `format<Feature>DocumentType` next to their page for exactly this job.
 *
 * WHY IT MAPS FROM `mime_type` ALONE. Those two sibling helpers also read
 * `doc.file_name`, which the operational documents wire does not have. The
 * only other candidate is `object_key`, and that is an internal storage path
 * — deriving a display value from it would both invent an API contract that
 * does not exist and risk putting a storage layout in front of a user. The
 * MIME type is declared, validated on upload, and is the honest source.
 *
 * NO CLASSNAMES LIVE HERE. The module returns an icon COMPONENT and a
 * label, never styling. That keeps the S2 theme guard's single-file scope
 * correct: every colour this feature names still sits in the page it
 * guards, and this file cannot introduce one behind its back.
 */
import {
  File,
  FileArchive,
  FileImage,
  FileSpreadsheet,
  FileText,
  FileType,
} from "lucide-react";

/**
 * A byte count, as a person reads it.
 *
 * MOVED HERE IN S6, unchanged. It lived in `AdminDocumentsPage.js` and is
 * now read from two places — the row and the upload dialog's "chosen file"
 * line — so it belongs beside the other display mapping rather than being
 * passed down as a prop or written twice. Byte-for-byte the same function:
 * same thresholds, same one decimal, same em dash for a missing size.
 */
export function formatBytes(n) {
  if (n == null) return "—";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}


/**
 * What a row shows when the MIME type is missing, empty or unrecognised.
 *
 * "Dokument" rather than "Unbekannt": the row IS a document — that much is
 * certain — and telling a reader their file is unknown helps nobody. The
 * generic icon carries the same message without the accusation.
 */
export const DOCUMENT_TYPE_FALLBACK = Object.freeze({
  key: "other",
  Icon: File,
  label: "Dokument",
  code: "DOC",
});

/**
 * Ordered, because the groups overlap and the FIRST match wins.
 *
 * `text/csv` is the case that makes the order load-bearing: it is a
 * spreadsheet to everyone who opens it and a text file only to a parser, so
 * the spreadsheet rule has to be asked first. Matching is on substrings
 * rather than exact strings because the office MIME types are long,
 * versioned and occasionally sent with a `; charset=` suffix — an exact
 * table would quietly fall through to the generic icon the first time a
 * browser added one.
 */
const TYPE_GROUPS = Object.freeze([
  {
    key: "pdf",
    Icon: FileText,
    label: "PDF",
    code: "PDF",
    matches: (m) => m.includes("pdf"),
  },
  {
    key: "image",
    Icon: FileImage,
    label: "Bild",
    code: "IMG",
    matches: (m) => m.startsWith("image/"),
  },
  {
    key: "spreadsheet",
    Icon: FileSpreadsheet,
    label: "Tabelle",
    code: "XLS",
    matches: (m) =>
      m.includes("spreadsheet") ||
      m.includes("ms-excel") ||
      m.includes("csv"),
  },
  {
    key: "word",
    Icon: FileType,
    label: "Word-Dokument",
    code: "DOC",
    matches: (m) =>
      m.includes("wordprocessingml") ||
      m.includes("msword") ||
      m.includes("opendocument.text"),
  },
  {
    key: "archive",
    Icon: FileArchive,
    label: "Archiv",
    code: "ZIP",
    matches: (m) =>
      m.includes("zip") ||
      m.includes("compressed") ||
      m.includes("x-tar") ||
      m.includes("x-7z") ||
      m.includes("rar"),
  },
  {
    key: "text",
    Icon: FileText,
    label: "Textdatei",
    code: "TXT",
    matches: (m) => m.startsWith("text/"),
  },
]);

/**
 * The icon and German label for one document's MIME type.
 *
 * TOTAL BY CONSTRUCTION. `null`, `undefined`, `""`, a number and an
 * unrecognised type all return the fallback rather than throwing. The wire
 * declares `mime_type` as a non-null string, so most of that is defence
 * rather than a live case — but this function is called once per row for
 * every row on the page, and a list that crashes on one odd legacy value
 * would take the other twenty-four rows with it.
 *
 * THE RAW MIME TYPE IS NEVER RETURNED. `application/vnd.openxmlformats-
 * officedocument.wordprocessingml.document` is not a thing to show a
 * person. Unrecognised types get "Dokument", not their own string.
 *
 * @param {unknown} mimeType the document's `mime_type`, as it arrives
 * @returns {{ key: string, Icon: Function, label: string }}
 */
export function documentTypeFor(mimeType) {
  const normalised = String(mimeType ?? "").trim().toLowerCase();
  if (!normalised) return DOCUMENT_TYPE_FALLBACK;
  const group = TYPE_GROUPS.find((g) => g.matches(normalised));
  if (!group) return DOCUMENT_TYPE_FALLBACK;
  return {
    key: group.key,
    Icon: group.Icon,
    label: group.label,
    // Derived from the group above, never from a second read of the MIME
    // string: the badge, the icon and the label are one decision.
    code: group.code,
  };
}
